import { useState } from 'react'
import {
  BarChart3,
  DollarSign,
  Users,
  FileText,
  Zap,
  RefreshCw,
  AlertCircle,
  HardDrive,
} from 'lucide-react'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts'
import { useOverallStats } from '@/hooks/useWorkspaces'
import { cn } from '@/lib/utils'

// ─── ユーティリティ ───

function formatBytes(bytes: number): string {
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`
  return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`
}

function formatTokens(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`
  return String(n)
}

// ─── サブコンポーネント ───

interface StatCardProps {
  title: string
  value: string
  sub?: string
  icon: React.ReactNode
  colorClass?: string
}

function StatCard({ title, value, sub, icon, colorClass = 'text-primary' }: StatCardProps) {
  return (
    <div className="rounded-lg border bg-card p-5 shadow-sm">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-muted-foreground">{title}</p>
          <p className={cn('mt-1 text-3xl font-bold', colorClass)}>{value}</p>
          {sub && <p className="mt-1 text-xs text-muted-foreground">{sub}</p>}
        </div>
        <div className={cn('rounded-full p-2', colorClass)}>{icon}</div>
      </div>
    </div>
  )
}

type Period = 'day' | 'week' | 'month'

const PERIOD_LABELS: Record<Period, string> = {
  day: '今日',
  week: '今週',
  month: '今月',
}

// ─── メインページ ───

export function StatsPage() {
  const [period, setPeriod] = useState<Period>('week')
  const { data, isLoading, error, refetch } = useOverallStats(period)

  const stats = data?.stats

  // 棒グラフ用にデータを整形（カテゴリ別の簡単な概要）
  const summaryBarData = stats
    ? [
        { label: 'ワークスペース', value: stats.totalWorkspaces },
        { label: 'ユーザー', value: stats.totalUsers },
        { label: 'ドキュメント', value: stats.totalDocuments },
        { label: '提案', value: stats.totalProposals },
      ]
    : []

  return (
    <div className="space-y-6">
      {/* ヘッダー */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">全体統計</h1>
          <p className="text-muted-foreground">利用量・コスト・パフォーマンスの概要</p>
        </div>
        <div className="flex items-center gap-3">
          {/* 期間切り替え */}
          <div className="flex rounded-lg border overflow-hidden">
            {(['day', 'week', 'month'] as Period[]).map((p) => (
              <button
                key={p}
                onClick={() => setPeriod(p)}
                className={cn(
                  'px-4 py-2 text-sm font-medium transition-colors',
                  period === p
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-background text-muted-foreground hover:bg-muted'
                )}
              >
                {PERIOD_LABELS[p]}
              </button>
            ))}
          </div>
          <button
            onClick={() => void refetch()}
            disabled={isLoading}
            className="flex items-center gap-2 rounded-lg border px-4 py-2 text-sm hover:bg-muted disabled:opacity-50"
          >
            <RefreshCw className={cn('h-4 w-4', isLoading && 'animate-spin')} />
            更新
          </button>
        </div>
      </div>

      {/* エラー */}
      {error && (
        <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-4 text-red-700">
          <AlertCircle className="h-5 w-5" />
          <span className="text-sm">統計データの取得に失敗しました</span>
        </div>
      )}

      {/* メトリクスカード */}
      {isLoading ? (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {[...Array(8)].map((_, i) => (
            <div key={i} className="h-28 animate-pulse rounded-lg border bg-card" />
          ))}
        </div>
      ) : stats ? (
        <>
          <section className="space-y-3">
            <h2 className="text-lg font-semibold">利用量</h2>
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
              <StatCard
                title="ワークスペース数"
                value={String(stats.totalWorkspaces)}
                icon={<BarChart3 className="h-5 w-5" />}
              />
              <StatCard
                title="ユーザー数"
                value={String(stats.totalUsers)}
                icon={<Users className="h-5 w-5" />}
              />
              <StatCard
                title="ドキュメント数"
                value={String(stats.totalDocuments)}
                icon={<FileText className="h-5 w-5" />}
              />
              <StatCard
                title="ストレージ使用量"
                value={formatBytes(stats.storageUsedBytes)}
                icon={<HardDrive className="h-5 w-5" />}
              />
            </div>
          </section>

          <section className="space-y-3">
            <h2 className="text-lg font-semibold">コスト</h2>
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
              <StatCard
                title="LLM トークン使用量"
                value={formatTokens(stats.llmTokensUsed)}
                sub="トークン合計"
                icon={<Zap className="h-5 w-5" />}
                colorClass="text-yellow-600"
              />
              <StatCard
                title="LLM コスト"
                value={`$${stats.llmCostUsd.toFixed(2)}`}
                sub="USD 合計"
                icon={<DollarSign className="h-5 w-5" />}
                colorClass="text-green-600"
              />
              <StatCard
                title="総提案数"
                value={String(stats.totalProposals)}
                sub={`採択率 ${(stats.proposalApprovalRate * 100).toFixed(1)}%`}
                icon={<FileText className="h-5 w-5" />}
                colorClass="text-blue-600"
              />
              <StatCard
                title="平均応答時間"
                value={`${stats.avgResponseMs.toFixed(0)} ms`}
                sub="API レスポンス"
                icon={<Zap className="h-5 w-5" />}
                colorClass={stats.avgResponseMs < 2000 ? 'text-green-600' : 'text-red-600'}
              />
            </div>
          </section>

          {/* サマリーバーチャート */}
          <section className="space-y-3">
            <h2 className="text-lg font-semibold">カテゴリ別件数</h2>
            <div className="rounded-lg border bg-card p-4 shadow-sm">
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={summaryBarData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Bar dataKey="value" name="件数" fill="#6366f1" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </section>
        </>
      ) : null}
    </div>
  )
}
